import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { SnackBarContext } from "../../context/SnackBarContext";
import { useTranslation } from "../../context/Internationalization";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import useStyles from "./formStyles";

/** =========================================================================
 * In-memory caches to prevent re-fetch storms across remounts:
 * - BASE_CACHE: key = JSON.stringify({query, queryVariables}), value = options[]
 * - TERM_CACHE: key = JSON.stringify({query, queryVariables, term}), value = options[]
 * - INFLIGHT: same key => Promise to dedupe concurrent requests
 * ========================================================================= */
const BASE_CACHE = new Map();
const TERM_CACHE = new Map();
const INFLIGHT = new Map();

const shallowEqualOptions = (a = [], b = []) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]?.value !== b[i]?.value) return false;
  }
  return true;
};

const AutocompleteRenderer = ({
  name,
  cacheKey,
  multiple,
  value,
  label,
  placeholder,
  onChange,
  query,
  queryVariables,
  mapResults,
  disabled,
  initialKey,
  error = "",
  selectAllButton = false,
  selectAllByDefault = false,
  /** re-filter in place when parent-supplied filter changes */
  filterOptionsFn, // (options) => filteredOptions
}) => {
  const styles = useStyles();
  const snackBar = useContext(SnackBarContext);
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  const [searchTerm, setSearchTerm] = useState("");
  /** Keep raw + filtered separately so we can re-apply filter anytime */
  const [rawOptions, setRawOptions] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const lastFetchedKeyRef = useRef(""); // "base" or term
  const hasPrefilledRef = useRef(false);
  const hasAutoSelectedAllRef = useRef(false);

  const getOptionLabelSafe = (option) =>
    typeof option === "string"
      ? option
      : option?.label ||
        option?.text?.props?.children?.[0]?.props?.children ||
        "";

  /** Helper to apply current filter */
  const applyFilter = (list) => {
    if (!Array.isArray(list)) return [];
    return typeof filterOptionsFn === "function" ? filterOptionsFn(list) : list;
  };

  /** Whenever the filter function OR raw options change, recompute options */
  useEffect(() => {
    const next = applyFilter(rawOptions);
    setOptions((prev) => (shallowEqualOptions(prev, next) ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptionsFn, rawOptions]);

  /** Keys for caches */
  // 1) Build a stable id: operation name OR field name
  const opName =
    (query && query.definitions && query.definitions[0]?.name?.value) || "";
  const cacheId = opName || `field:${name}`; // never "static"

  // 2) Keep variables in the key
  const queryVarsKey = useMemo(
    () => JSON.stringify(queryVariables ?? {}),
    [queryVariables]
  );

  // 3) Keys for caches/in-flight
  const baseKey = useMemo(
    () => JSON.stringify({ id: cacheId, v: queryVarsKey }),
    [cacheId, queryVarsKey]
  );
  const termKey = (term) =>
    JSON.stringify({ id: cacheId, v: queryVarsKey, term });

  /** ---- Fetchers with cache & in-flight dedupe ---- */
  const fetchBase = async () => {
    const key = "base";

    // 1) Serve from cache
    if (BASE_CACHE.has(baseKey)) {
      const cached = BASE_CACHE.get(baseKey);
      // only update if different
      if (!shallowEqualOptions(rawOptions, cached)) {
        setRawOptions(cached);
      }
      lastFetchedKeyRef.current = key;
      return;
    }

    // 2) Dedup in-flight
    if (INFLIGHT.has(baseKey)) {
      await INFLIGHT.get(baseKey);
      const cached = BASE_CACHE.get(baseKey) || [];
      if (!shallowEqualOptions(rawOptions, cached)) {
        setRawOptions(cached);
      }
      lastFetchedKeyRef.current = key;
      return;
    }

    setLoading(true);
    const p = (async () => {
      try {
        const res = await axiosGraphQL({
          query,
          variables: { ...queryVariables },
        });
        const results = mapResults(res.data.data) || [];
        BASE_CACHE.set(baseKey, results);
        // filtered options will be recomputed via effect
        if (!shallowEqualOptions(rawOptions, results)) {
          setRawOptions(results);
        }

        lastFetchedKeyRef.current = key;

        // Auto-select all if requested and nothing chosen yet (use current filtered list)
        const filtered = applyFilter(results);
        if (
          multiple &&
          selectAllByDefault &&
          !hasAutoSelectedAllRef.current &&
          Array.isArray(filtered) &&
          filtered.length > 0 &&
          (!Array.isArray(value) || value.length === 0)
        ) {
          hasAutoSelectedAllRef.current = true;
          onChange({ target: { name, value: [...filtered] } });
        }
      } catch (error) {
        errorLog(error, "AutocompleteRenderer", "fetchBase", query);
        snackBar.setOptions({
          alertMessage: error.message,
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
      } finally {
        setLoading(false);
        INFLIGHT.delete(baseKey);
      }
    })();

    INFLIGHT.set(baseKey, p);
    await p;
  };

  const fetchByTerm = async (term) => {
    if (!term) return fetchBase();
    if (lastFetchedKeyRef.current === term) return; // avoid duplicate fetch

    const tKey = termKey(term);

    // 1) Cache
    if (TERM_CACHE.has(tKey)) {
      const cached = TERM_CACHE.get(tKey);
      if (!shallowEqualOptions(rawOptions, cached)) {
        setRawOptions(cached);
      }
      lastFetchedKeyRef.current = term;
      return;
    }

    // 2) In-flight dedupe
    if (INFLIGHT.has(tKey)) {
      await INFLIGHT.get(tKey);
      const cached = TERM_CACHE.get(tKey) || [];
      if (!shallowEqualOptions(rawOptions, cached)) {
        setRawOptions(cached);
      }
      lastFetchedKeyRef.current = term;
      return;
    }

    setLoading(true);
    const p = (async () => {
      try {
        const res = await axiosGraphQL({
          query,
          variables: { term, ...queryVariables },
        });
        const results = mapResults(res.data.data) || [];
        TERM_CACHE.set(tKey, results);
        if (!shallowEqualOptions(rawOptions, results)) {
          setRawOptions(results);
        }
        lastFetchedKeyRef.current = term;
      } catch (error) {
        errorLog(error, "AutocompleteRenderer", "fetchByTerm", query);
        snackBar.setOptions({
          alertMessage: error.message,
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
      } finally {
        setLoading(false);
        INFLIGHT.delete(tKey);
      }
    })();

    INFLIGHT.set(tKey, p);
    await p;
  };

  /** ---- Prefill options by current value (ids -> objects) ---- */
  useEffect(() => {
    if (!value || hasPrefilledRef.current) return;

    const valuesArray = multiple
      ? value.map((v) => v?.value || v).filter(Boolean)
      : [value?.value || value].filter(Boolean);

    if (valuesArray.length === 0) return;

    (async () => {
      setLoading(true);
      try {
        // seed base cache if available (helps reduce flicker)
        if (BASE_CACHE.has(baseKey) && rawOptions.length === 0) {
          setRawOptions(BASE_CACHE.get(baseKey));
        }

        const response = await axiosGraphQL({
          query,
          variables: { ...queryVariables, [initialKey]: valuesArray },
        });
        const results = mapResults(response.data.data) || [];

        // Merge into rawOptions (by unique .value)
        setRawOptions((prev) => {
          const seen = new Set(prev.map((o) => o.value));
          const merged = [...prev];
          for (const r of results) {
            if (!seen.has(r.value)) merged.push(r);
          }
          // Also merge into BASE_CACHE to survive remounts
          const cached = BASE_CACHE.get(baseKey) || [];
          const seenC = new Set(cached.map((o) => o.value));
          const mergedC = [...cached];
          for (const r of results) {
            if (!seenC.has(r.value)) mergedC.push(r);
          }
          BASE_CACHE.set(baseKey, mergedC);
          return merged;
        });

        hasPrefilledRef.current = true;
      } catch (error) {
        errorLog(error, "AutocompleteRenderer", "initialOptionsFetch", query);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ---- Initial base load ---- */
  useEffect(() => {
    // reset last key to allow a new term fetch after scope change
    lastFetchedKeyRef.current = "";
    fetchBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey]);

  /** ---- Debounced search: < 3 chars => base; ≥ 3 => term search ---- */
  useEffect(() => {
    const handle = setTimeout(() => {
      const term = (searchTerm || "").trim();
      if (term.length >= 3) {
        fetchByTerm(term);
      } else {
        fetchBase();
      }
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, baseKey]);

  /** ---- Handlers ---- */
  const handleChange = (_event, newValue) => {
    const formatted = multiple ? newValue || [] : newValue || null;
    onChange({ target: { name, value: formatted } });
  };

  const handleInputChange = (_event, newInputValue, reason) => {
    if (reason === "reset" || reason === "clear") {
      setSearchTerm("");
      if (reason === "clear") {
        onChange({ target: { name, value: multiple ? [] : "" } });
      }
    } else {
      setSearchTerm(newInputValue || "");
    }
  };

  const handleOpen = () => {
    // Ensure we have something to show when opening (especially after a selection)
    if (rawOptions.length === 0 && !loading) {
      fetchBase();
    }
  };

  return (
    <Stack direction="column" spacing={0.5} sx={{ width: "100%" }}>
      <span className={styles.label}>{label}</span>
      {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      {multiple && (options.length > 0 || value?.length > 0) && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} mb={1}>
          {selectAllButton && (
            <Button
              onClick={() =>
                onChange({ target: { name, value: [...options] } })
              }
              disabled={
                loading ||
                (Array.isArray(value) &&
                  value.length === options.length &&
                  value.every((v) =>
                    options.some((opt) => opt.value === v.value)
                  ))
              }
              size="small"
              sx={{ alignSelf: "flex-end" }}
            >
              {t("select_all")}
            </Button>
          )}
          {selectAllButton && (
            <Button
              onClick={() => onChange({ target: { name, value: [] } })}
              disabled={loading || !value?.length}
              size="small"
              sx={{ alignSelf: "flex-end" }}
            >
              {t("clear_all")}
            </Button>
          )}
        </Stack>
      )}

      <Autocomplete
        key={multiple ? "multi" : "single"}
        multiple={multiple}
        disablePortal
        fullWidth
        value={
          multiple
            ? Array.isArray(value)
              ? value
              : []
            : value && value !== ""
            ? value
            : null
        }
        inputValue={undefined}
        options={options}
        filterOptions={(x) => x} // we manage filtering server-side / via filterOptionsFn
        getOptionLabel={getOptionLabelSafe}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.text || option.label}
          </li>
        )}
        isOptionEqualToValue={(option, val) => option?.value === val?.value}
        onOpen={handleOpen}
        onChange={handleChange}
        onInputChange={handleInputChange}
        disabled={disabled}
        noOptionsText={t("no_results")}
        renderTags={
          multiple
            ? (tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  const labelSafe = getOptionLabelSafe(option);
                  return <Chip key={key} label={labelSafe} {...chipProps} />;
                })
            : undefined
        }
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!error}
            helperText={error}
            placeholder={placeholder}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": { borderColor: styles.input.borderColor },
                "&:hover fieldset": { borderColor: styles.input.borderColor },
                "&.Mui-focused fieldset": {
                  borderColor: styles.input.borderColor,
                },
              },
              "& .MuiOutlinedInput-input": { paddingBottom: "22px" },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Stack>
  );
};

AutocompleteRenderer.propTypes = {};

export default React.memo(AutocompleteRenderer);
